'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement } from 'react'

import { faAngleLeft } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import pluralize from 'pluralize'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { UnstyledButton } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { Button, FormattedMessage, Modal } from '@b-stock/fe-core/bstock-react'
import { Colors, pxToRem, Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'
import type { StoreFrontDto } from '@b-stock/listing-api-client'

import StyledScroll from '@/components/common/modal/scrollbarStyles'
import { ModalFooter } from '@/components/common/modal/ui'
import { FreezeSortToggle } from '@/components/listings/ListView/FreezeSortToggle'
import FilterCheckbox from '@/components/listings/search/SearchFilters/shared/FilterCheckbox'
import {
  FilterHeaderSelectAll,
  ModalStatistic,
  TotalSelected,
} from '@/components/listings/search/SearchFilters/shared/Modal'
import { FeatureFlags } from '@/helpers/featureFlags'

import type {
  ListingFiltersState,
  useListingSearch,
} from '../ListingSearchProvider'
import type { ThresholdStep } from '../ListingSearchProvider/categoryThreshold'
import SortByDropdown from '../sortByDropdown/SortByDropdown'
import StorefrontListingSearch from '../StorefrontListingSearch'
import {
  StagedThresholdPanel,
  StagedThresholdSummaryNote,
  useTrackThresholdAdjusted,
} from './filters/CategoryFilter/CategoryThresholdControl'
import { FiltersModalContent } from './FiltersModalContent'
import ShowResultsButton from './shared/ShowResultsButton'
import type { ListingFilterList, SelectedFilterType } from './types'

type FilterValuesHandler = <K extends keyof ListingFiltersState>(
  key: K,
  values: ListingFiltersState[keyof ListingFiltersState & K]
) => void

const StyledIcon = styled(FontAwesomeIcon)`
  font-size: 1rem;
  color: ${Colors.Semantic.BStock.background.darkBackground};
`

const LeftArrow = styled(UnstyledButton)`
  margin-right: 1rem;
`

const Divider = styled.div`
  border-bottom: ${pxToRem(1)} solid ${Colors.Semantic.Neutral.medium_grey};
`

// flex: 1 1 auto makes this section fill the remaining space in ModalContainer's
// flex column, so the Cancel/Show Results footer stays pinned to the panel's
// bottom edge instead of sitting right after whatever content happens to render.
// min-height: 0 + overflow-y: auto let it scroll internally, rather than
// dragging Header/Footer along with it, once content does exceed the panel.
const ModalContent = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 1.5rem;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  padding: 1.5rem 1rem;
  scrollbar-gutter: stable;

  ${StyledScroll}
`

// Right-anchored slide-in panel used for the desktop list view. Width follows the
// established sidebar-modal pattern (see ManifestSummaryPopup).
const PanelModal = styled(Modal)`
  width: clamp(26rem, 30vw, 42rem);
`

// Aligns the panel title's left edge with the filter names in ModalContent (1rem).
const PanelHeader = styled(Modal.Header)`
  padding: 1.5rem 1rem;
`

const FilterSeparateHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`

const PinnedThresholdArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border-bottom: ${pxToRem(1)} solid ${Colors.Semantic.Neutral.medium_grey};
`

const TotalSelectedNote = styled.span`
  ${Typography.Body4};
  color: ${designColors.neutral.veryDarkGray};
  margin-left: 0.5rem;
`

const FilterTitleRow = styled.div`
  display: flex;
  align-items: center;
`

const BackButton = ({ onPress }: { onPress: () => void }): ReactElement => (
  <LeftArrow onClick={onPress}>
    <StyledIcon icon={faAngleLeft} />
  </LeftArrow>
)

function FiltersModal({
  closeFilterModal,
  filters,
  searchCtx,
  showFilterModal,
  showFreezeSort,
  showRelevance,
  showSortBy,
  showStorefrontSearch,
  slideInPanel = false,
  storefront,
}: {
  closeFilterModal: () => void
  filters: ListingFilterList
  searchCtx: ReturnType<typeof useListingSearch>
  showFilterModal: boolean
  /** List View only (GLOB-4832 BP-2421) — Grid View callers should omit this. */
  showFreezeSort?: boolean
  showRelevance?: boolean
  showSortBy?: boolean
  showStorefrontSearch?: boolean
  /** When true, render as a right-anchored slide-in panel (desktop list view). */
  slideInPanel?: boolean
  storefront?: StoreFrontDto
}) {
  const intl = useIntl()

  const [filterValues, setFilterValues] = useState<
    Partial<ListingFiltersState>
  >(searchCtx.state.filters)

  useEffect(
    () => {
      setFilterValues(searchCtx.state.filters) // oxlint-disable-line react/set-state-in-effect
    },
    // Reset filterValues when modal opens/closes or applied filters change
    [showFilterModal, searchCtx.state.filters]
  )

  const handleFilterValues: FilterValuesHandler = useCallback(
    (key, values) => {
      setFilterValues((prev) => ({
        ...prev,
        [key]: values,
      }))
    },
    [] // setFilterValues is stable
  )

  const handleThresholdChange = useCallback(
    (step: ThresholdStep) => handleFilterValues('categoryThreshold', step),
    [handleFilterValues]
  )
  const trackThresholdAdjusted = useTrackThresholdAdjusted(searchCtx)
  const enableCategoryThreshold = useFlag(
    FeatureFlags.ENABLE_CATEGORY_COMPOSITION_THRESHOLD
  )

  const [hasError, setHasError] = useState(false)
  const modalContentRef = useRef<HTMLDivElement>(null)

  const [selectedFilter, setSelectedFilter] = useState<SelectedFilterType>({
    filterLabel: '',
    itemsCount: 0,
  })

  const selectFilter = ({ filterLabel, itemsCount }: SelectedFilterType) => {
    setSelectedFilter({ filterLabel, itemsCount })
  }

  const handleBackButtonClick = () => {
    setSelectedFilter({ filterLabel: '', itemsCount: 0 })
  }

  const filterShouldDisplaySeparately = !!(
    selectedFilter.filterLabel && selectedFilter.itemsCount > 8
  )

  const localSearchCtx = useMemo(
    () => ({
      ...searchCtx,
      state: {
        ...searchCtx.state,
        filters: { ...searchCtx.state.filters, ...filterValues },
      },
    }),
    [searchCtx, filterValues]
  )

  const separateFilterComp = filterShouldDisplaySeparately
    ? filters.find((f) => f.label === selectedFilter.filterLabel)
    : undefined
  const separateFilterProps = separateFilterComp?.getOwnProps(localSearchCtx)
  // Raw items from getOwnProps have { value: string } shape (before useFilterOptions maps value → key)
  const separateFilterItems = separateFilterProps?.items as
    | Array<{ value?: string }>
    | undefined
  const separateFilterSet = (() => {
    const val: unknown = separateFilterProps?.value
    if (val instanceof Set) return val as Set<string>
    if (Array.isArray(val)) return new Set(val as string[])
    return undefined
  })()
  const separateFilterTitle = separateFilterComp
    ? intl.formatMessage({ id: separateFilterComp.label })
    : ''

  const isItemSelected = (itemValue: string) => {
    if (!separateFilterSet) return false
    return separateFilterComp?.isItemCoveredBySelectedSet
      ? separateFilterComp.isItemCoveredBySelectedSet(
          itemValue,
          separateFilterSet
        )
      : separateFilterSet.has(itemValue)
  }

  // For hierarchical filters (e.g. RegionFilter), getSelectAllValues returns compressed parent
  // keys (like /CA, /US). Use those as the "all selected" check items so that the header
  // checkbox correctly shows "checked" after Select All, even when separateFilterItems contains
  // zero-count entries whose values aren't directly in the selected set.
  const separateAllCheckItems =
    separateFilterComp?.getSelectAllValues && separateFilterItems
      ? separateFilterComp
          .getSelectAllValues(separateFilterItems)
          .map((v) => ({ value: v }))
      : separateFilterItems

  const separateAllSelected =
    !!separateFilterSet &&
    !!separateAllCheckItems &&
    separateAllCheckItems.length > 0 &&
    separateAllCheckItems.every((i) => !!i.value && isItemSelected(i.value))
  const separateSomeSelected =
    !separateAllSelected &&
    !!separateFilterSet &&
    !!separateFilterItems &&
    separateFilterItems.some((i) => !!i.value && isItemSelected(i.value))

  const separateFilterKey = separateFilterProps?.filterKey

  // oxlint-disable-next-line react/preserve-manual-memoization
  const handleSeparateSelectAll = useCallback(() => {
    if (!separateFilterKey || !separateFilterItems) return
    const newValue: string[] = separateAllSelected
      ? []
      : (separateFilterComp?.getSelectAllValues?.(separateFilterItems) ??
        separateFilterItems.map((i) => i.value).filter((v): v is string => !!v))
    handleFilterValues(
      separateFilterKey,
      newValue as unknown as ListingFiltersState[keyof ListingFiltersState]
    )
  }, [
    separateAllSelected, // oxlint-disable-line react/preserve-manual-memoization
    separateFilterComp, // oxlint-disable-line react/preserve-manual-memoization
    separateFilterItems, // oxlint-disable-line react/preserve-manual-memoization
    separateFilterKey, // oxlint-disable-line react/preserve-manual-memoization
    handleFilterValues,
  ])

  const hasSortSection = showSortBy || showStorefrontSearch || showFreezeSort

  const showThresholdPanel =
    enableCategoryThreshold &&
    filterShouldDisplaySeparately &&
    !!separateFilterComp?.hasCategoryThreshold

  const separateSelectAll = separateFilterSet && (
    <FilterHeaderSelectAll>
      <FilterCheckbox
        checked={
          separateAllSelected ? true : separateSomeSelected ? 'PARTIAL' : false
        }
        label={<FormattedMessage id="Filters.modal.selectAll" />}
        onChange={handleSeparateSelectAll}
      />
    </FilterHeaderSelectAll>
  )

  if (!showFilterModal) return null

  const ModalRoot = slideInPanel ? PanelModal : Modal
  const HeaderRoot = slideInPanel ? PanelHeader : Modal.Header

  return (
    <ModalRoot
      appearance={slideInPanel ? 'sidebar' : 'standard'}
      closeModal={closeFilterModal}
    >
      <HeaderRoot>
        {filterShouldDisplaySeparately ? (
          showThresholdPanel ? (
            // The panel and "Select all" get their own pinned strip below
            <FilterTitleRow>
              <BackButton onPress={handleBackButtonClick} />
              <FormattedMessage id={selectedFilter.filterLabel} />
            </FilterTitleRow>
          ) : (
            <FilterSeparateHeader>
              <FilterTitleRow>
                <BackButton onPress={handleBackButtonClick} />
                <FormattedMessage id={selectedFilter.filterLabel} />
              </FilterTitleRow>
              {separateSelectAll}
            </FilterSeparateHeader>
          )
        ) : (
          <FormattedMessage
            id={
              hasSortSection
                ? 'Filters.MobileSearchFilters.header'
                : 'Filters.MobileSearchFilters.filtersOnlyHeader'
            }
          />
        )}
      </HeaderRoot>
      {showThresholdPanel && (
        // Mirrors the category modal: Lot composition, then "Select all",
        // both pinned above the scrolling list.
        <PinnedThresholdArea>
          <StagedThresholdPanel
            filterValues={filterValues}
            onChange={handleThresholdChange}
          />
          {separateSelectAll}
        </PinnedThresholdArea>
      )}
      <ModalContent ref={modalContentRef}>
        {!filterShouldDisplaySeparately && hasSortSection && (
          <>
            {showStorefrontSearch && storefront && (
              <StorefrontListingSearch
                applyOnSubmit={false}
                onChange={(value) => handleFilterValues('q', value)}
                storefront={storefront}
              />
            )}
            {showSortBy && <SortByDropdown showRelevance={!!showRelevance} />}
            {showFreezeSort && (
              <FreezeSortToggle pageType={storefront ? 'storefront' : 'alp'} />
            )}
            <Divider />
          </>
        )}
        <FiltersModalContent
          containerRef={modalContentRef}
          filterShouldDisplaySeparately={filterShouldDisplaySeparately}
          filters={filters}
          filterValues={filterValues}
          onFilterChange={handleFilterValues}
          onFilterError={setHasError}
          searchCtx={searchCtx}
          selectedFilter={selectedFilter.filterLabel}
          selectFilter={selectFilter}
        />
      </ModalContent>
      <ModalFooter>
        {filterShouldDisplaySeparately && separateFilterSet && (
          <ModalStatistic>
            <TotalSelected>
              <FormattedMessage
                id="Filters.modal.list"
                values={{
                  amount: separateFilterSet.size,
                  name: pluralize(separateFilterTitle, separateFilterSet.size),
                }}
              />
              {separateFilterComp?.hasCategoryThreshold && (
                <TotalSelectedNote>
                  <StagedThresholdSummaryNote filterValues={filterValues} />
                </TotalSelectedNote>
              )}
            </TotalSelected>
          </ModalStatistic>
        )}
        <Modal.Actions>
          <Button
            appearance="tertiary"
            onClick={closeFilterModal}
            type="button"
          >
            <FormattedMessage id="Filters.MobileSearchFilters.actions.cancel" />
          </Button>
          <ShowResultsButton
            hasError={hasError}
            loading={false}
            onSubmit={() => {
              trackThresholdAdjusted(filterValues.categoryThreshold)
              searchCtx.updateFilters(filterValues)
              closeFilterModal()
            }}
            results={searchCtx.data?.total.toString() || '0'}
          />
        </Modal.Actions>
      </ModalFooter>
    </ModalRoot>
  )
}

export default FiltersModal
